import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { RatingScaleLegend, ItemRow, MIN_NOTE_CHARS } from "../components/Rating";

export default function CalibrationRate() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [scores, setScores] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/calibrations/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(apiError(e, "Not found")));
  }, [id]);

  if (!c) return <div className="p-10 font-mono-cal text-xs">[LOADING…]</div>;

  if (c.manager_submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="cal-card p-10 text-center relative overflow-hidden">
          <div className="locked-pattern" />
          <Lock className="mx-auto mb-4" size={40} />
          <h2 className="font-display font-bold text-3xl mb-2">Your rating is sealed</h2>
          <p className="text-[#666] mb-6">Waiting on the employee to submit. The overlay unlocks once both sides are done.</p>
          <button onClick={() => nav(`/calibrations/${id}`)} className="cal-btn" data-testid="rate-back">← Back</button>
        </div>
      </div>
    );
  }

  const items = [
    ...c.tasks_snapshot.map((t) => ({ ...t, kind: "TASK" })),
    ...(c.employee_goals || []).map((g) => ({ ...g, kind: "EMPLOYEE GOAL" })),
  ];

  const setScore = (itemId, score) => setScores((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), score } }));
  const setNotes = (itemId, notes) => setScores((s) => ({ ...s, [itemId]: { ...(s[itemId] || { score: 3 }), notes } }));

  const allScored = items.every((it) => scores[it.id]?.score);
  const allNoted = items.every((it) => ((scores[it.id]?.notes || "").trim().length >= MIN_NOTE_CHARS));

  const submit = async () => {
    if (!allScored) { toast.error("Rate every item before submitting"); return; }
    if (!allNoted) { toast.error(`Every rating needs at least ${MIN_NOTE_CHARS} characters of evidence`); return; }
    setSubmitting(true);
    try {
      await api.post(`/calibrations/${id}/submit-manager`, {
        scores: items.map((it) => ({ item_id: it.id, score: scores[it.id].score, notes: scores[it.id].notes || "" }))
      });
      toast.success("Submitted. Awaiting the employee…");
      nav(`/calibrations/${id}`);
    } catch (err) { toast.error(apiError(err, "Submit failed")); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666] mb-3">[BLIND] Manager assessment</div>
      <h1 className="font-display font-bold text-5xl mb-2">{c.title}</h1>
      <p className="text-[#666] mb-4">Rating {c.employee_name} · {c.period_label} · {items.length} items ({c.tasks_snapshot.length}T + {(c.employee_goals || []).length}G)</p>

      <div className="cal-card p-4 mb-6 flex items-center gap-3" style={{ background: "#FF4F00", color: "#fff", borderColor: "#FF4F00" }} data-testid="privacy-banner">
        <Lock size={18} style={{ color: "#fff" }} />
        <div className="font-mono-cal text-xs uppercase tracking-widest" style={{ color: "#fff" }}>BLIND MODE — Employee self-ratings are sealed until you submit</div>
      </div>

      <RatingScaleLegend />

      <div className="space-y-5">
        {items.map((it, i) => (
          <ItemRow key={it.id} idx={i} kind={it.kind} description={it.description}
            score={scores[it.id]?.score} notes={scores[it.id]?.notes}
            onScore={(n) => setScore(it.id, n)} onNotes={(n) => setNotes(it.id, n)}
            testPrefix="mgr" />
        ))}
      </div>

      <div className="mt-10 flex items-center gap-4">
        <button onClick={() => nav(`/calibrations/${id}`)} className="cal-btn cal-btn-ghost" data-testid="rate-cancel">Cancel</button>
        <div className="flex-1 font-mono-cal text-xs text-[#666]">
          {Object.keys(scores).filter((k) => scores[k]?.score).length} / {items.length} rated · {items.filter((it) => ((scores[it.id]?.notes || "").trim().length >= MIN_NOTE_CHARS)).length} / {items.length} substantiated
        </div>
        <button onClick={submit} disabled={!allScored || !allNoted || submitting} className="cal-btn" data-testid="submit-rating">
          {submitting ? "Submitting…" : "Submit & seal →"}
        </button>
      </div>
    </div>
  );
}
